import { Deferred, Head, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { DashboardColdStart } from "@/components/brand/dashboard/dashboard-cold-start"
import { DashboardDeadlinesPanel } from "@/components/brand/dashboard/dashboard-deadlines-panel"
import { DashboardDeadlinesSkeleton } from "@/components/brand/dashboard/dashboard-deadlines-skeleton"
import { DashboardErrorState } from "@/components/brand/dashboard/dashboard-error-state"
import { DashboardKpiSkeleton } from "@/components/brand/dashboard/dashboard-kpi-skeleton"
import { DashboardKpiStrip } from "@/components/brand/dashboard/dashboard-kpi-strip"
import { CandidatesAwaitingValidationSection } from "@/components/candidates/candidates-awaiting-validation-section"
import { CandidatesToContactSection } from "@/components/candidates/candidates-to-contact-section"
import { CandidatesToFollowUpSection } from "@/components/candidates/candidates-to-follow-up-section"
import { CandidatesToReviewSection } from "@/components/candidates/candidates-to-review-section"
import { InvitesGoingQuietSection } from "@/components/candidates/invites-going-quiet-section"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { SubmissionsToReviewSection } from "@/components/submissions/submissions-to-review-section"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useRetainedDeferredSection } from "@/hooks/use-retained-deferred-section"
import AppLayout from "@/layouts/app-layout"
import type { BrandDashboardProps } from "@/types/brand-dashboard"

/**
 * OKS-38 — Brand dashboard home (`/brand`).
 *
 * Replaces the OKS-7 placeholder grid with the V1-minimum operational
 * snapshot: 4 KPI tiles + Upcoming Deadlines panel.
 *
 * # State branch (top-down — TRD §4 page composition)
 *
 *   1. `isRetrying` (local, set during the Retry round-trip)
 *      → render KPI + Deadlines skeletons
 *   2. `error === true` (controller `dashboard_props` rescued)
 *      → render `<DashboardErrorState>` with Retry button
 *   3. `cold_start === true` (zero crew, zero missions, never had a mission)
 *      → render `<DashboardColdStart>` with "Create your first mission" CTA
 *   4. otherwise (success / edge — `kpis` + `upcoming_deadlines` populated)
 *      → render `<DashboardKpiStrip>` + `<DashboardDeadlinesPanel>`
 *
 * # Retry round-trip (TRD §K20)
 *
 * The Retry button calls `router.reload({ only: [data props] })` — an
 * Inertia partial reload that re-renders the dashboard props without a
 * full navigation (shell stays mounted). Local `isRetrying` toggles the
 * skeleton swap; `onFinish` clears it back to false regardless of
 * success/error so a persistent failure doesn't leave the surface stuck
 * on the skeleton (the next render will re-show the error state with
 * `error: true`).
 */
export default function BrandHome({
  cold_start: coldStart,
  error,
  kpis,
  upcoming_deadlines: upcomingDeadlines,
  candidates_to_review: candidatesToReview,
  candidates_awaiting_validation: candidatesAwaitingValidation,
  candidates_to_follow_up: candidatesToFollowUp,
  candidates_to_contact: candidatesToContact,
  candidates_invites_quiet: candidatesInvitesQuiet,
  submissions_to_review: submissionsToReview,
}: BrandDashboardProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)
  const { currentBrand } = useCurrentContext()

  // OKS-923 — keep the last resolved submissions section so the `<Deferred>`
  // fallback can render the same component type (see the section below).
  // Keyed on the acting brand: the retained rows are discarded rather than
  // re-attributed if the brand ever changes underneath a mounted page.
  const retainedSubmissions = useRetainedDeferredSection(submissionsToReview, {
    identity: currentBrand?.id,
  })

  function handleRetry() {
    setIsRetrying(true)
    // OKS-706 — page-level Retry refreshes the dashboard data AND the
    // triage section together (UX Spec §2); the section also carries its
    // own inline Retry for a section-only failure.
    router.reload({
      only: [
        "cold_start",
        "error",
        "kpis",
        "upcoming_deadlines",
        "candidates_to_review",
        "submissions_to_review",
      ],
      onFinish: () => setIsRetrying(false),
    })
  }

  return (
    <AppLayout>
      <Head title={t("brand_home.head_title")} />
      <div className="flex flex-col gap-6 p-4 md:p-6" data-testid="brand-home">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("brand_home.title")}
          </h1>
        </header>

        {renderBody({
          isRetrying,
          error,
          coldStart,
          kpis,
          upcomingDeadlines,
          onRetry: handleRetry,
        })}

        {/*
          OKS-706 — Candidates-to-review triage section. Rendered below
          the dashboard body and INDEPENDENT of the mission cold-start /
          error branch above (TRD KD1) — recruiting is the pre-mission
          activity. Deferred prop: the `<Deferred>` fallback is the shell's
          loading skeleton until the section resolves.
        */}
        <Deferred
          data="candidates_to_review"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_review.title")}
              state="loading"
              count={0}
              testId="candidates-to-review"
            />
          }
        >
          {candidatesToReview ? (
            <CandidatesToReviewSection section={candidatesToReview} />
          ) : null}
        </Deferred>

        {/*
          OKS-950 — "En attente de votre validation". Sits directly BELOW
          Candidates-to-review, and the order is the pipeline order: the
          agency screens above, the brand signs off below.

          A SEPARATE section rather than extra rows in the one above. That
          queue is the agency's work in progress; showing its rows here would
          ask a brand manager to decide on candidates nobody has screened yet.
          The server keeps them apart by passing a different `statuses:`
          argument, never by widening a shared constant (PRD AC4).

          Independently deferred and independently rescued — a failure in
          either section degrades that section, not the home.
        */}
        <Deferred
          data="candidates_awaiting_validation"
          fallback={
            <HomeTriageSection
              title={t("candidates.awaiting_validation.title")}
              state="loading"
              count={0}
              testId="candidates-awaiting-validation"
            />
          }
        >
          {candidatesAwaitingValidation ? (
            <CandidatesAwaitingValidationSection
              section={candidatesAwaitingValidation}
            />
          ) : null}
        </Deferred>

        {/*
          OKS-951 — « À relancer ». The FOURTH triage section, sibling to the
          three above, independently deferred and independently rescued so a
          failure here degrades one section rather than the home.

          Per PO this is the most-used segment in the product. It sits after the
          two decision queues deliberately: those are "someone is waiting on
          you", this is "someone has gone quiet on you" — a different kind of
          work, and the decision queues are time-sensitive in a way chasing is
          not.

          The fallback renders the SAME component type as the resolved branch
          (`HomeTriageSection`), per the OKS-923 finding: `<Deferred>` resets to
          its fallback on every same-URL visit, so a different type there makes
          React unmount the subtree on each round-trip.
        */}
        <Deferred
          data="candidates_to_follow_up"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_follow_up.title")}
              state="loading"
              count={0}
              testId="home-follow-up"
            />
          }
        >
          {candidatesToFollowUp ? (
            <CandidatesToFollowUpSection section={candidatesToFollowUp} />
          ) : null}
        </Deferred>

        {/*
          OKS-953 — segments 3 and 5. Pipeline order on BOTH homes: an agency
          manager and a brand manager talking about "the third section" must
          mean the same thing (UX Spec §2A).
        */}
        <Deferred
          data="candidates_to_contact"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_to_contact.title")}
              state="loading"
              count={0}
              testId="home-to-contact"
            />
          }
        >
          {candidatesToContact ? (
            <CandidatesToContactSection section={candidatesToContact} />
          ) : null}
        </Deferred>

        <Deferred
          data="candidates_invites_quiet"
          fallback={
            <HomeTriageSection
              title={t("candidates.home_invite_stale.title")}
              state="loading"
              count={0}
              testId="home-invite-stale"
            />
          }
        >
          {candidatesInvitesQuiet ? (
            <InvitesGoingQuietSection section={candidatesInvitesQuiet} />
          ) : null}
        </Deferred>

        {/*
          OKS-707 — Submissions-to-review triage section. Rendered directly
          BELOW Candidates-to-review as a sibling (UX Spec §2), with its own
          props bucket + own 5-state rendering, independent of the mission
          cold-start / error branch (TRD KD2).

          OKS-923 — both `<Deferred>` branches render THE SAME COMPONENT TYPE
          once there is something to keep. `<Deferred>` renders `fallback` and
          `children` at one position and resets to the fallback on every
          same-URL visit, so a different type in the fallback made React
          unmount the whole subtree on each round-trip — taking the open flag
          dialog and the user's typed note with it, which is why a correctable
          error had nowhere to render here. Same type ⇒ React reconciles ⇒ the
          dialog survives.

          The skeleton below is still correct for the FIRST resolve, when there
          are no rows to keep. That is deliberate, not an inconsistency to tidy
          up later (PRD §5).
        */}
        <Deferred
          data="submissions_to_review"
          fallback={
            retainedSubmissions.section ? (
              <SubmissionsToReviewSection
                section={retainedSubmissions.section}
                isRefreshing={retainedSubmissions.isRefreshing}
                refreshFailed={retainedSubmissions.refreshFailed}
              />
            ) : (
              <HomeTriageSection
                title={t("submissions.home_review.title")}
                state="loading"
                count={0}
                testId="submissions-to-review"
              />
            )
          }
        >
          {submissionsToReview ? (
            <SubmissionsToReviewSection section={submissionsToReview} />
          ) : null}
        </Deferred>
      </div>
    </AppLayout>
  )
}

interface RenderBodyArgs {
  isRetrying: boolean
  error: boolean
  coldStart: boolean
  kpis: BrandDashboardProps["kpis"]
  upcomingDeadlines: BrandDashboardProps["upcoming_deadlines"]
  onRetry: () => void
}

function renderBody({
  isRetrying,
  error,
  coldStart,
  kpis,
  upcomingDeadlines,
  onRetry,
}: RenderBodyArgs) {
  // 1) Loading — local; takes precedence so the skeleton appears
  // immediately on Retry click, not after the round-trip lands.
  if (isRetrying) {
    return (
      <div className="flex flex-col gap-6">
        <DashboardKpiSkeleton />
        <DashboardDeadlinesSkeleton />
      </div>
    )
  }

  // 2) Error — full-surface replacement.
  if (error) {
    return <DashboardErrorState onRetry={onRetry} isRetrying={isRetrying} />
  }

  // 3) Cold-start — full-surface replacement.
  if (coldStart) {
    return <DashboardColdStart />
  }

  // 4) Success / edge — KPI + deadlines. Defensive null-coalesce in case
  // a future controller branch sets `cold_start=false` without populating
  // data props; the page should never crash, only render zeros.
  return (
    <div className="flex flex-col gap-6">
      <DashboardKpiStrip
        kpis={
          kpis ?? {
            active_crew: 0,
            active_missions: 0,
            completion_rate: null,
            content_this_month: 0,
          }
        }
      />
      <DashboardDeadlinesPanel rows={upcomingDeadlines ?? []} />
    </div>
  )
}
